import { lazy, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../common/components/Card";
import { DefaultButton } from '../common/components/DefaultButton';
import { Desktop, DesktopComponentRegistry } from '../common/components/Desktop';
import { Col, Row } from "../common/components/Grid";
import FormEditor from "../common/components/GridEditor";
import { ResizeAwareDiv } from "../common/components/ResizeablePanel";
import TextareaInput from "../common/components/TextareaInput";
import useClientConfig from '../common/hooks/clientConfigHook';
import useCredentials from "../common/hooks/credentialsHook";
import { useListDefs } from '../reducers/listSlice';
import { CADProvider, useCADPopout } from "../utils/CADStateProvider";
import { Suspense } from "react";
import { Spinner } from "react-bootstrap";
import { SitRepContext } from "./SitRep";
import { updateJob } from "../reducers/jobSlice";
import useNetwork from "../common/hooks/networkHook";
import { checkEnabled } from "../utils/enableUtilities";

const FormEditorTest = () => {
    const [testDef, setTestDef] = useState([]);
    
    const handleChange = useCallback(value => {
        try {
            setTestDef(JSON.parse(value));
        } catch {

        }
    }, []);

    return <>
        <Row>
            <Col>
                <Card title="JSON">
                    <Row>
                        <Col>
                            <TextareaInput rows="5" placeholder="JSON" value={JSON.stringify(testDef, null, 2)} onChange={handleChange}></TextareaInput>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col className="text-right">
                            <DefaultButton kind="copy" onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(testDef, null, 2))
                            }}></DefaultButton>
                        </Col>
                    </Row>
                </Card>
            </Col>
        </Row>
        <Row>
            <Col>
                <FormEditor def={testDef} onChange={setTestDef}></FormEditor> 
            </Col>
        </Row>
    </>
}

const epcrOnlyLayouts = listDefs => [
    {
        id: 0,
        title: '1 Column',
        defaultNarrow: true,
        columns: [
            { rows: [{ id: 'main', components: ['independentEpcrPanel', 'epcrSearchPanel', 'refDocPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 100, id: 1 }
        ]
    },
    {
        id: 1,
        title: '2 Column',
        narrow: false,
        defaultMedium: true,
        defaultWide: true,
        columns: [
            { rows: [{ id: 'left', components: ['independentEpcrPanel', 'epcrSearchPanel', 'refDocPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 33, id: 2 },
            { rows: [{ id: 'main', components: ['epcrPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 66, id: 1}
        ]
    }
];

const trackerOnlyLayouts = listDefs => [
    {
        id: 0,
        title: '1 Column',
        defaultNarrow: true,
        columns: [
            { rows: [{ id: 'main', components: ['geoMap'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 100, id: 1}
        ]
    },
    {
        id: 1,
        title: '2 Column',
        narrow: false,
        defaultMedium: true,
        defaultWide: true,
        columns: [
            { rows: [{ id: 'left', components: ['crewListPanel', 'refDocPanel', 'locationDetailsPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 33, id: 2 },
            { rows: [{ id: 'main', components: ['geoMap'], default: { component: 'geoMap' }, proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 66, id: 1}
        ]
    },
    {
        id: 3,
        title: '3 Column',
        narrow: false,
        medium: false,
        columns: [
            { rows: [{ id: 'left', components: ['crewListPanel', 'refDocPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 33, id: 2 },
            { rows: [{ id: 'main', components: ['geoMap'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 33, id: 1},
            { rows: [{ id: 'right', components: ['locationDetailsPanel', 'weatherMap'], proportion: 100 }], proportion: 33, id: 3 }
        ]
    }
]

const fullLayouts = (listDefs, enableCrews) => [
    {
        id: 0,
        title: '1 Column',
        defaultNarrow: true,
        columns: [
            { rows: [{ id: 'main', components: ['activeJobPanel'], proportion: 90 }, 
                     { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }
                    ], proportion: 100, id: 1 }
        ]
    },
    {
        id: 1,
        title: '2 Column',
        narrow: false,
        defaultMedium: true,
        defaultWide: !enableCrews,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'crewListPanel', 'locationDetailsPanel', 'jobSearchPanel', 'glossaryPanel', 'refDocPanel', 'eventInfoPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel', 'jobProcedurePanel', 'scheduledJobListPanel', 'independentEpcrPanel', 'epcrSearchPanel', 'logPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 33, id: 2 },
            { rows: [
                { id: 'main', components: ['jobPanel'], proportion: 90 },
                { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }
                ], proportion: 66, id: 1 }
        ]
    },
    {
        id: 2,
        title: '2 Column - Left Detail',
        narrow: false,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'crewListPanel', 'jobSearchPanel', 'scheduledJobListPanel', 'independentEpcrPanel', 'epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 66 },
                     { id: 'bottomLeft', components: ['weatherMap', 'locationDetailsPanel', 'eventInfoPanel', 'glossaryPanel', 'refDocPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel', 'jobProcedurePanel', 'logPanel'], proportion: 33 }
                    ], proportion: 33, id: 2 },
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 66, id: 1}
        ]
    },
    {
        id: 3,
        title: '3 Column',
        narrow: false,
        medium: false,
        defaultWide: enableCrews,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'locationDetailsPanel', 'jobSearchPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 25, id: 2 },
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 50, id: 1},
            { rows: [{ id: 'right', components: ['crewListPanel', 'jobProcedurePanel', 'eventInfoPanel', 'glossaryPanel', 'refDocPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel','logPanel'], proportion: 100 }], proportion: 25, id: 3 }
        ]
    },
    {
        id: 4,
        title: '3 Column - Right Detail',
        narrow: false,
        medium: false,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'jobSearchPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 100 }], proportion: 33, id: 2},
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 33, id: 1},
            { rows: [{ id: 'right', components: ['crewListPanel', 'jobProcedurePanel', 'eventInfoPanel', 'glossaryPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel'], proportion: 66 },
                     { id: 'bottomRight', components: ['weatherMap', 'locationDetailsPanel', 'eventInfoPanel', 'glossaryPanel', 'refDocPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel', 'jobProcedurePanel', 'logPanel'], proportion: 33 }
                    ], proportion: 33, id: 3}
        ]
    },
    {
        id: 5,
        title: '3 Column - Left Detail ',
        narrow: false,
        medium: false,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'jobSearchPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel','logPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 66 },
                     { id: 'bottomLeft', components: ['weatherMap', 'locationDetailsPanel', 'eventInfoPanel', 'glossaryPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel', 'jobProcedurePanel'], proportion: 33 }
                    ], proportion: 33, id: 2},
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 33, id: 1},
            { rows: [{ id: 'right', components: ['crewListPanel', 'jobProcedurePanel', 'eventInfoPanel', 'glossaryPanel', 'refDocPanel', 'messagingPanel', 'taskListPanel', 'contactListPanel'], proportion: 100 }], proportion: 33, id: 3}
        ]
    },
    {
        id: 6,
        title: '3 Column - Both Detail',
        medium: false,
        narrow: false,
        columns: [
            { rows: [{ id: 'left',  components: ['activeJobPanel', 'jobSearchPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 66 },
                     { id: 'bottomLeft', components: ['weatherMap', 'locationDetailsPanel'], proportion: 33 }
                    ], proportion: 33, id: 2},
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 33, id: 1},
            { rows: [{ id: 'right', components: ['crewListPanel', 'messagingPanel', 'taskListPanel','logPanel'], proportion: 66 },
                     { id: 'bottomRight', components: ['jobProcedurePanel', 'eventInfoPanel', 'glossaryPanel','contactListPanel'], proportion: 33 }
                    ], proportion: 33, id: 3}
        ]
    },
    {
        id: 7,
        title: '3 Column - Extra Detail',
        medium: false,
        narrow: false,
        columns: [
            { rows: [{ id: 'left', components: ['activeJobPanel', 'jobSearchPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 66 },
                     { id: 'bottomLeft', components: ['weatherMap', 'locationDetailsPanel'], proportion: 33 }
                    ], proportion: 33, id: 2},
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 33, id: 1},
            { rows: [{ id: 'topRight', components: ['jobProcedurePanel', 'eventInfoPanel', 'logPanel'], proportion: 33},
                     { id: 'right', components: ['crewListPanel', 'messagingPanel', 'taskListPanel'], proportion: 33 },
                     { id: 'bottomRight', components: ['glossaryPanel','contactListPanel'], proportion: 33 }
                    ], proportion: 33, id: 3}
        ]
    },
    {
        id: 8,
        title: '4 Panel',
        medium: false,
        narrow: false,
        columns: [
            { rows: [{ id: 'topLeft', components: ['activeJobPanel','scheduledJobListPanel','independentEpcrPanel','epcrSearchPanel', ...(listDefs.map(l => `list_${l.id}`))], proportion: 50 },
                     { id: 'bottomLeft', components: ['crewListPanel','messagingPanel','taskListPanel','logPanel','eventInfoPanel','locationDetailsPanel','glossaryPanel','contactListPanel'], proportion: 50 }], proportion: 50, id: 2},
            { rows: [{ id: 'main', components: ['jobPanel'], proportion: 45},
                     { id: 'bottomRight', components: ['geoMap','weatherMap','jobProcedurePanel'], proportion: 45},
                     { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 50, id: 1 }
        ]
    }
]

const makeRegistry = (t, clientConfig, credentials, listDefs) => {
    const registry = new DesktopComponentRegistry();
    if (clientConfig.desktop?.taskbars?.length > 0 && clientConfig.desktop?.taskbar) {
        registry.register({ sid: 'toolbar', component: lazy(() => import('./TaskToolbar')), offline: true, fixed: true })
    }
    if (clientConfig.enableLists) {
        registry.addList({ id: 2, title: t('knowledgeBase'), icon: { variant: 'solid', kind: 'boxes' }});
        listDefs?.forEach(def => {
            registry.register({
                sid: 'list_' + def.id,
                label: def.name,
                component: lazy(() => import('./ListPanel')),
                args: { id: def.id },
                list: 2,
                offline: def.available_offline
            });
            registry.register({
                sid: 'stats_' + def.id,
                component: lazy(() => import('./stats/ListStats')),
                args: { listid: def.id },
                list: null,
                offline: def.available_offline
            });
        });
        registry.register({
            sid: 'listEntryPanel',
            component: lazy(() => import('./ListEntryPanel')),
            offline: true
        });
    }
    registry.addList(
        { id: 1, title: t('tools'), icon: { variant: 'solid', kind: 'screwdriver-wrench' } }
    );

    if (clientConfig.trackerOnly) {
            registry.register({ sid: 'crewListPanel', component: lazy(() => import('./CrewListPanel')), label: t('crew', { count: 100 }), icon: { variant: 'solid', kind: 'users' }, primary: true, list: 1 })
            registry.register({ sid: 'crewPanel', component: lazy(() => import('./CrewPanel')), label: t('crewDetails'), icon: { variant: 'regular', kind: 'address-card' }, primary: false, list: 1 })
        if (clientConfig.enableMapping) {
            registry.register({ sid: 'geoMap', component: lazy(() => import('./GeoMap')), label: t('map'), icon: { variant: 'solid', kind: 'map-location-dot' }, primary: true, list: 1 });
            if (clientConfig.enableWeather) {
                registry.register({ sid: 'weatherMap', component: lazy(() => import('./WeatherMap')), label: t('weatherMap'), icon: { variant: 'solid', kind: 'cloud-sun' }, primary: true, singleton: true, list: 1 });
            }
        }
        if (clientConfig.enableRefDocs) {
            registry.register({ sid: 'refDocPanel', component: lazy(() => import('./RefDocPanel')), label: t('referenceDocument', { count: 100 }), icon: { variant: 'solid', kind: 'folder-tree' }, primary: false, list: 1 });
        }
        registry.register({ sid: 'locationDetailsPanel', component: lazy(() => import('./LocationDetailsPanel')), label: t('locationDetails', { count: 100 }), icon: { variant: 'solid', kind: 'fa-magnifying-glass-location' }, primary: false, list: 1 });
        return registry;
    }
    if (clientConfig.enablePCR && clientConfig.epcrOnly) {
        if (clientConfig.hasIndependentEPCR) {
            registry.register({ sid: 'independentEpcrPanel', offline: true, component: lazy(() => import('./IndependentEpcrPanel')), label: t('independentEPCRs'), icon: { variant: 'solid', kind: 'book-medical' }, primary: true, list: 1 });
        }
        registry.register({ sid: 'epcrPanel', offline: true, component: lazy(() => import('./epcr/EpcrPanel')), label: t('epcrDetails'), icon: { variant: 'solid', kind: 'file-medical' }, primary: true, list: 1 });
        if (credentials.access.has(['administrator','dispatcher'])) {
            registry.register({ sid: 'epcrSearchPanel', component: lazy(() => import('./epcr/EpcrSearch')), label: t('epcrSearch'), icon: { variant: 'solid', kind: 'magnifying-glass-plus' }, primary: false, list: 1 });
        }
        if (credentials.access.has(['administrator', 'dispatcher'])) {
            registry.register({ sid: 'epcrMedicationSearchPanel', component: lazy(() => import('./epcr/medication/EpcrMedicationSearch')), label: t('epcrMedicationSearch'), icon: { variant: 'solid', kind: 'magnifying-glass-plus' }, primary: false, list: 1 });
        }
        if (clientConfig.enableRefDocs) {
            registry.register({ sid: 'refDocPanel', component: lazy(() => import('./RefDocPanel')), label: t('referenceDocument', { count: 100 }), icon: { variant: 'solid', kind: 'folder-tree' }, primary: false, list: 1 });
        }
        return registry;
    }
    registry.register({ sid: 'activeJobPanel', component: lazy(() => import('./ActiveJobPanel')), label: t('activeJobs'), icon: { variant: 'solid', kind: 'table-list' }, primary: true, offline: true, list: 1 });
    registry.register({ sid: 'jobPanel', component: lazy(() => import('./JobWrapperPanel')), label: t('jobDetails'), icon: { variant: 'regular', kind: 'file-lines' }, primary: true, offline: true, list: 1 });
    registry.register({ sid: 'jobSearchPanel', component: lazy(async () => ({ default: (await import('./JobSearchPanel')).JobSearchPanel })), label: t('jobSearch'), icon: { variant: 'solid', kind: 'magnifying-glass' }, primary: true, list: 1, });
    //{ component: FormEditorTest, label: "Form Editor", icon: { variant: 'regular', kind: 'file-lines' }, primary: true, list: 1 }
    if (clientConfig.enableCrews && !clientConfig.hidrecrews) {
        registry.register({ sid: 'crewListPanel', component: lazy(() => import('./CrewListPanel')), label: t('crew', { count: 100 }), icon: { variant: 'solid', kind: 'users' }, primary: true, list: 1 });
        registry.register({ sid: 'crewPanel', component: lazy(() => import('./CrewPanel')), label: t('crewDetails'), icon: { variant: 'regular', kind: 'address-card' }, primary: false, list: 1 });
    }
    if (clientConfig.enableMapping) {
        registry.register({ sid: 'geoMap', component: lazy(() => import('./GeoMap')), label: t('map'), icon: { variant: 'solid', kind: 'map-location-dot' }, primary: true, list: 1 });
        if (clientConfig.enableWeather) {
            registry.register({ sid: 'weatherMap', component: lazy(() => import('./WeatherMap')), label: t('weatherMap'), icon: { variant: 'solid', kind: 'cloud-sun' }, primary: true, singleton: true, list: 1 });
        }
    }
    if (clientConfig.enableLog && (credentials.access.has('administrator') || !credentials.log.none())) {
        registry.register({ sid: 'logPanel', component: lazy(() => import('./LogPanel')), label: t('log', { count: 2 }), icon: { variant: 'solid', kind: 'scroll' }, primary: false, list: 1 });
    }
    if (clientConfig.enableGlossary) {
        registry.register({ sid: 'glossaryPanel', component: lazy(() => import('./GlossaryPanel')), label: t('glossary'), icon: { variant: 'solid', kind: 'spell-check' }, primary: false, list: 1 });
    }
    if (clientConfig.enableEvents === true) {
        registry.register({ sid: 'eventInfoPanel', component: lazy(async() => ({ default: (await import('./EventInfo')).EventInfoPanel })), label: t('eventInfo'), icon: { variant: 'solid', kind: 'circle-info' }, primary: false, list: 1 });
    }
    registry.register({ sid: 'locationDetailsPanel', component: lazy(() => import('./LocationDetailsPanel')), label: t('locationDetails'), icon: { variant: 'solid', kind: 'fa-magnifying-glass-location' }, primary: false, list: 1 });
    registry.register({ sid: 'messagingPanel', component: lazy(() => import('./MessagingPanel')), label: t('messaging'), icon: {variant: 'solid', kind: 'envelope'}, primary: false, list: 1 })
    if (clientConfig.enableTaskLists) {
        registry.register({ sid: 'taskListPanel', component: lazy(async () => ({ default: (await import('./TaskListPanel')).TaskListPanel })), label: t('taskList', { count: 100 }), icon: { variant: 'regular', kind: 'rectangle-list' }, primary: false, list: 1 });
    }
    registry.register({ sid: 'contactListPanel', component: lazy(() => import('./ContactListPanel')), label: t('contact', { count: 100 }), icon: { variant: 'solid', kind: 'address-book' }, primary: false, list: 1 });
    if (clientConfig.enableJobProcedures) {
        registry.register({ sid: 'jobProcedurePanel', component: lazy(() => import('./JobProcedurePanel')), label: t('sop', { count: 100 }), icon: { variant: 'solid', kind: 'list-check' }, primary: false, list: 1 });
    }
    if (clientConfig.enableRefDocs) {
        registry.register({ sid: 'refDocPanel', component: lazy(() => import('./RefDocPanel')), label: t('referenceDocument', { count: 100 }), icon: { variant: 'solid', kind: 'folder-tree' }, primary: false, list: 1 });
    }
    if (checkEnabled(clientConfig, clientConfig.decision?.enable)) {
        registry.register({ sid: 'decisionPanel', component: lazy(() => import('./DecisionPanel')), label: t('decision', { count: 100 }), icon: { variant: 'solid', kind: 'scale-balanced' }, primary: false, list: 1 })
    }
    registry.register({ sid: 'scheduledJobListPanel', component: lazy(() => import('./ScheduledJobListPanel')), label: t('scheduledJob', { count: 100 }), icon: { variant: 'regular', kind: 'calendar-days' }, primary: false, list: 1 })
    registry.register({ sid: 'scheduledJobPanel', component: lazy(() => import('./ScheduledJobPanel')), label: t('scheduledJobDetails'), icon: { variant: 'solid', kind: 'calendar-day' }, primary: false, list: 1 })
    if (clientConfig.enablePCR) {
        if (clientConfig.hasIndependentEPCR) {
            registry.register({ sid: 'independentEpcrPanel', component: lazy(() => import('./IndependentEpcrPanel')), label: t('independentEPCRs'), icon: { variant: 'solid', kind: 'book-medical' }, primary: true, list: 1, offline: true });
        }
        registry.register({ sid: 'epcrPanel', component: lazy(() => import('./epcr/EpcrPanel')), label: t('epcrDetails'), icon: { variant: 'solid', kind: 'file-medical' }, primary: true, list: 1, offline: true });
        if (credentials.access.has(['administrator','dispatcher'])) {
            registry.register({ sid: 'epcrSearchPanel', component: lazy(() => import('./epcr/EpcrSearch')), label: t('epcrSearch'), icon: { variant: 'solid', kind: 'magnifying-glass-plus' }, primary: false, list: 1 });
        }
        if (credentials.access.has(['administrator', 'dispatcher'])) {
            registry.register({ sid: 'epcrMedicationSearchPanel', component: lazy(() => import('./epcr/medication/EpcrMedicationSearch')), label: t('epcrMedicationSearch'), icon: { variant: 'solid', kind: 'magnifying-glass-plus' }, primary: false, list: 1 });
        }
    }
    if (clientConfig.ai?.chat) {
        registry.register({ sid: 'aiChatPanel', component: lazy(async () => ({ default: (await import('./AIChatPanel')).AIChatPanel })), label: t('aiName'), icon: { variant: 'solid', kind: 'infinity' }, primary: false, list: 1 })
    }
    if (clientConfig.desktop?.components?.['sitRepList']) {
        registry.register({ sid: 'sitRepList', component: lazy(async () => ({ default: (await import('./SitRep')).SitRepList })), label: t('sitrep'), icon: { variant: 'solid', kind: 'table-list' }, list: 1 });
    }
    if (clientConfig.desktop?.components?.['sitRepDetails']) {
        registry.register({ sid: 'sitRepDetails', component: lazy(async () => ({ default: (await import('./SitRep')).SitRepDetails })), label: t('sitrepDetails'), icon: { variant: 'solid', kind: 'table-list' }, list: 1 });
    }
    if (clientConfig.desktop?.components?.['weatherInfo']) {
        registry.register({ sid: 'weatherInfo', component: lazy(async () => import('./WeatherWidget')), label: t('currentWeather'), icon: { variant: 'solid', kind: 'cloud' }, primary: false, list: 1 });
    }
    if (clientConfig.desktop?.components?.['threatLevel']) {
        registry.register({ sid: 'threatLevel', display: { minSize: 75, maxSize: 75 }, component: lazy(async () => import('./ThreatLevelBanner')), label: t('ukThreatLevelLabel'), icon: { variant: 'solid', kind: 'shield' }, primary: false, list: 1})
    }
    registry.register({ sid: 'alertInfoPanel', display: { minSize: 75, maxSize: 75 }, component: lazy(async () => ({ default: (await import('./AlertInfo')).AlertPanel })), label: t('alertInfo'), icon: { variant: 'solid', kind: 'triangle-exclamation' }, primary: false, list: 1 })
    if (clientConfig.desktop?.components?.['lineStatusPanel']) {
        registry.register({ sid: 'lineStatusPanel', component: lazy(async () => import('./LineStatus')), label: t('lineStatusPanel'), icon: { variant: 'solid', kind: 'train' }, primary: false, list: 1 })
    }
    if (clientConfig.desktop?.components?.['jobStats']) {
        registry.register({ sid: 'jobStats', component: lazy(async () => import('./stats/JobStatsPanel')), label: t('stats'), icon: { variant: 'solid', kind: 'chart-simple' }, primary: false, list: 1 })
    }
    // if (clientConfig.wiki) {
    //     registry.register({ sid: 'manual', label: t('manual'), icon: { variant: 'solid',  kind: 'book' }, onClick: () => {
    //         let domain = window.location.hostname.indexOf('.') > -1 ? window.location.hostname.slice(window.location.hostname.indexOf('.')) : '.chronosoft.wiki';
    //         fetch(`https://wiki-chronicler${domain}/login?legacy=1`, {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json'
    //             },
    //             body: JSON.stringify({
    //                 strategy: 'local',
    //                 user: clientConfig.wiki.username,
    //                 pass: clientConfig.wiki.password
    //             }),
    //             credentials: 'include'
    //         }).then(res => {
    //             window.open(`https://wiki-chronicler${domain}/${clientConfig.wiki.page ?? ''}`)
    //         });
    //     }, list: 1, primary: false })
    // }

    
    return registry;
}

const CADImpl = () => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const listDefs = useListDefs();
    const popout = useCADPopout();

    const components = useMemo(() => makeRegistry(t, clientConfig, credentials, listDefs), [t, clientConfig, credentials, listDefs]);
    const layouts = useMemo(() => {
        let layouts;
        if (clientConfig.epcrOnly) {
            layouts = epcrOnlyLayouts(listDefs);
        } else if (clientConfig.trackerOnly) {
            layouts = trackerOnlyLayouts(listDefs);
        } else {
            layouts = fullLayouts(listDefs, clientConfig.enableCrews && !clientConfig.hidecrews);
        }
        if (clientConfig.desktop?.layouts?.length > 0) {
            for (const layout of layouts) {
                delete layout.defaultNarrow;
                delete layout.defaultMedium;
                delete layout.defaultWide;
            }
            for (const layout of clientConfig.desktop.layouts) {
                const idx = layouts.findIndex(l => l.id === layout.id);
                const toPush = { ...layout };
                if (toPush.narrow === undefined && toPush.medium === undefined && toPush.wide === undefined) {
                    toPush.narrow = false;
                    toPush.medium = false;
                }
                if (idx > -1) {
                    layouts.splice(idx, 1, toPush.columns ? toPush : undefined);
                } else if (toPush.columns) {
                    layouts.push(toPush);
                }
            }
        }
        return layouts;
    }, [listDefs, clientConfig.epcrOnly, clientConfig.trackerOnly, clientConfig.desktop, clientConfig.enableCrews, clientConfig.hidecrews]);

    const onPopout = useCallback((sid, args) => {
        popout(sid, args);
    }, [popout]);

    const handlers = useMemo(() => {
        return {
            locate: { component: 'geoMap' },
            createJob: data => {
                updateJob(network, data);
                return { component: 'jobPanel' }
            },
            selectJob: { component: 'jobPanel' },
            selectCrew: { component: 'crewPanel' },
            selectScheduledJob: { component: 'scheduledJobPanel' },
            selectListEntry: { component: 'listEntryPanel' },
            selectList: data => ({ component: 'list_' + data.id }),
            selectRequestableJob: { component: 'jobPanel' },
            selectListStats: data => ({ component: 'stats_' + data.id }),
            selectLocation: { component: 'locationDetailsPanel' },
            selectPCR: data => {
                if (data.pcrid) {
                    return { component: 'epcrPanel' }
                } else if (!clientConfig.epcrOnly) {
                    return { swap: ['epcrPanel', 'jobPanel'] };
                }
            },
            ...(clientConfig.desktop?.handlers ?? {})
        }
    }, [clientConfig, network]);

    return <ResizeAwareDiv>
        <Desktop id="cad" key="desktop" components={components} layouts={layouts} handlers={handlers} taskbarCollapse onPopout={onPopout}>
        </Desktop>
    </ResizeAwareDiv>;
}

export const AngularCAD = () => {
    return <div style={{height: 'calc(100dvh - 60px)', overflow: 'auto', paddingTop: '10px'}}>
        <CADProvider>
            <SitRepContext>
                <CADImpl></CADImpl>
            </SitRepContext>
        </CADProvider>
    </div>
}
export const CAD = () => <CADProvider><SitRepContext><CADImpl></CADImpl></SitRepContext></CADProvider>;

export const CADPopout = ({ cadState, sid, args }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const listDefs = useListDefs();

    const registry = useMemo(() => makeRegistry(t, clientConfig, credentials, listDefs), [t, clientConfig, credentials, listDefs]);
    const Clazz = useMemo(() => registry.getComponent(sid)?.component, [registry, sid]);

    return <CADProvider init={cadState}>
        <SitRepContext>
            <Suspense fallback={<Spinner></Spinner>}>
                {Clazz && <Clazz {...args}></Clazz>}
            </Suspense>
        </SitRepContext>
    </CADProvider>
}